import Psd from '@webtoon/psd';
import type { API } from '@/types';

const pngScaleStep = 0.8;
const pngMinSide = 100;
export const dressOriginMaxBytes = 2 * 1024 * 1024;
export const dressThumbMaxBytes = 200 * 1024;

type PsdNode = {
  type: string;
  name?: string;
  title?: string;
  visible?: boolean;
  hidden?: boolean;
  isHidden?: boolean;
  layerFrame?: {
    layerProperties?: {
      hidden?: boolean;
    };
  };
  children?: PsdNode[];
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  composite?: (...args: unknown[]) => Promise<Uint8ClampedArray | Uint8Array>;
};

type PsdRoot = PsdNode & {
  width: number;
  height: number;
};

type LayerEntry = {
  node: PsdNode;
  groupPath: string[];
  order: number;
};

export type PsdBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PsdCanvasSize = {
  width: number;
  height: number;
};

export type ParsedDressItem = {
  clientItemId: string;
  psdFileName: string;
  layerName: string;
  fileName: string;
  groupPath: string[];
  bounds: PsdBounds;
  contentBounds: PsdBounds;
  canvas: PsdCanvasSize;
  order: number;
  originBlob: Blob;
  originUrl: string;
  dressTypeId: string;
  dressTypeName: string;
  recognized: boolean;
  deleted: boolean;
  thumbBlob?: Blob;
  thumbUrl?: string;
  creativeType: number;
  fitType: number;
  submitStatus?: 'idle' | 'submitting' | 'submitted' | 'failed';
  submitMessage?: string;
};

export type PsdParseProgress = {
  stage: 'read' | 'parse' | 'export' | 'complete';
  current?: number;
  total?: number;
  message: string;
};

export type ThumbnailTransform = {
  offsetX?: number;
  offsetY?: number;
};

type ParseOptions = {
  file: File;
  dressTypes: API.DressType[];
  maxSourceMb?: number;
  maxLayerCount?: number;
  onProgress?: (progress: PsdParseProgress) => void;
};

const defaultMaxSourceMb = 150;
const defaultMaxLayerCount = 300;
const alphaThreshold = 8;

const normalizeName = (value: string) =>
  value
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[＿_·\-—（）()[\]【】]/g, '');

const getNodeName = (node: PsdNode) => node.name || node.title || '未命名图层';

const isVisible = (node: PsdNode) =>
  node.visible !== false &&
  node.hidden !== true &&
  node.isHidden !== true &&
  node.layerFrame?.layerProperties?.hidden !== true;

const sanitizeFileName = (name: string) => {
  const normalized = name.trim() || '未命名图层';
  return normalized.replace(/[\\/:*?"<>|]/g, '_');
};

const buildUniqueFileName = (name: string, usedNames: Map<string, number>) => {
  const safeName = sanitizeFileName(name);
  const count = usedNames.get(safeName) || 0;
  usedNames.set(safeName, count + 1);
  return count === 0 ? `${safeName}.png` : `${safeName}_${count + 1}.png`;
};

const collectLayers = (node: PsdNode, groupPath: string[], parentVisible: boolean, output: LayerEntry[]) => {
  const currentVisible = parentVisible && isVisible(node);
  if (!currentVisible) return;

  if (node.type === 'Group') {
    const nextPath = [...groupPath, getNodeName(node)];
    node.children?.forEach((child) => collectLayers(child, nextPath, currentVisible, output));
    return;
  }

  if (node.type === 'Psd') {
    node.children?.forEach((child) => collectLayers(child, groupPath, currentVisible, output));
    return;
  }

  if (node.type === 'Layer') {
    output.push({ node, groupPath, order: output.length });
  }
};

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('PNG Blob 生成失败'));
    }, 'image/png');
  });

export const revokeParsedItemUrls = (items: ParsedDressItem[]) => {
  items.forEach((item) => {
    URL.revokeObjectURL(item.originUrl);
    if (item.thumbUrl) URL.revokeObjectURL(item.thumbUrl);
  });
};

const getAlphaBounds = (imageData: ImageData): PsdBounds | null => {
  const { data, width, height } = imageData;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha <= alphaThreshold) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (maxX < minX || maxY < minY) return null;
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
};

const createLayerCanvas = async (layer: PsdNode, canvas: PsdCanvasSize) => {
  const layerWidth = Math.max(0, Math.round(layer.width || 0));
  const layerHeight = Math.max(0, Math.round(layer.height || 0));
  const x = Math.round(layer.left || 0);
  const y = Math.round(layer.top || 0);
  if (!layerWidth || !layerHeight || !layer.composite) return null;

  const pixels = await layer.composite(false);
  if (!pixels?.length) return null;

  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = canvas.width;
  outputCanvas.height = canvas.height;
  const context = outputCanvas.getContext('2d');
  if (!context) throw new Error('Canvas 初始化失败');

  if (pixels.length === canvas.width * canvas.height * 4) {
    context.putImageData(new ImageData(new Uint8ClampedArray(pixels), canvas.width, canvas.height), 0, 0);
  } else if (pixels.length === layerWidth * layerHeight * 4) {
    context.putImageData(new ImageData(new Uint8ClampedArray(pixels), layerWidth, layerHeight), x, y);
  } else {
    throw new Error(`图层 ${getNodeName(layer)} 像素尺寸异常`);
  }

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const contentBounds = getAlphaBounds(imageData);
  if (!contentBounds) {
    outputCanvas.width = 0;
    outputCanvas.height = 0;
    return null;
  }

  return {
    canvas: outputCanvas,
    bounds: { x, y, width: layerWidth, height: layerHeight },
    contentBounds,
  };
};

const matchDressType = (groupPath: string[], layerName: string, dressTypes: API.DressType[]) => {
  const typed = dressTypes.map((type) => ({
    id: type.id,
    name: type.name,
    normalized: normalizeName(type.name),
  })).filter((type) => type.normalized);
  const candidates = [...groupPath].reverse().concat(layerName).map(normalizeName).filter(Boolean);

  const exact = typed.find((type) => candidates.some((candidate) => candidate === type.normalized));
  if (exact) return { id: exact.id, name: exact.name, recognized: true };

  const contains = typed.find((type) =>
    candidates.some((candidate) => candidate.includes(type.normalized) || type.normalized.includes(candidate))
  );
  if (contains) return { id: contains.id, name: contains.name, recognized: true };

  return { id: '', name: '未识别', recognized: false };
};

export const parsePsdFile = async ({ file, dressTypes, maxSourceMb = defaultMaxSourceMb, maxLayerCount = defaultMaxLayerCount, onProgress }: ParseOptions) => {
  if (!file.name.toLowerCase().endsWith('.psd')) throw new Error('文件格式有误，请上传PSD格式的文件');
  if (file.size > maxSourceMb * 1024 * 1024) throw new Error(`最大支持${maxSourceMb}M的PSD文件`);

  onProgress?.({ stage: 'read', message: '正在读取 PSD' });
  const buffer = await file.arrayBuffer();
  onProgress?.({ stage: 'parse', message: '正在解析 PSD' });
  let psd: PsdRoot;
  try {
    psd = Psd.parse(buffer) as unknown as PsdRoot;
  } catch {
    throw new Error('无法解析 PSD 文件，请检查文件是否完整，或重新导出后再试');
  }
  const canvas = { width: Math.round(psd.width || 0), height: Math.round(psd.height || 0) };
  if (!canvas.width || !canvas.height) throw new Error('无法读取 PSD 画布尺寸');

  const layers: LayerEntry[] = [];
  collectLayers(psd, [], true, layers);
  if (!layers.length) throw new Error('未识别到可导出的普通图层');
  if (layers.length > maxLayerCount) throw new Error(`PSD图层数量不能超过${maxLayerCount}个`);

  const usedNames = new Map<string, number>();
  const items: ParsedDressItem[] = [];
  for (let index = 0; index < layers.length; index += 1) {
    const entry = layers[index];
    const layerName = getNodeName(entry.node);
    onProgress?.({ stage: 'export', current: index + 1, total: layers.length, message: `正在导出 ${layerName}` });
    const layerCanvas = await createLayerCanvas(entry.node, canvas);
    if (!layerCanvas) continue;

    const originBlob = await canvasToBlob(layerCanvas.canvas);
    const match = matchDressType(entry.groupPath, layerName, dressTypes);
    items.push({
      clientItemId: `item_${Date.now()}_${index + 1}`,
      psdFileName: file.name,
      layerName,
      fileName: buildUniqueFileName(layerName, usedNames),
      groupPath: entry.groupPath,
      bounds: layerCanvas.bounds,
      contentBounds: layerCanvas.contentBounds,
      canvas,
      order: entry.order,
      originBlob,
      originUrl: URL.createObjectURL(originBlob),
      dressTypeId: match.id,
      dressTypeName: match.name,
      recognized: match.recognized,
      deleted: false,
      creativeType: 1,
      fitType: 0,
      submitStatus: 'idle',
    });
    layerCanvas.canvas.width = 0;
    layerCanvas.canvas.height = 0;
    await new Promise((resolve) => window.setTimeout(resolve, 0));
  }

  if (!items.length) throw new Error('未导出任何有效 PNG');
  onProgress?.({ stage: 'complete', current: items.length, total: items.length, message: 'PSD 解析完成' });
  return { canvas, items };
};

const loadImageFromBlob = async (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const generateThumbnail = async (
  sourceBlob: Blob,
  bounds: PsdBounds,
  canvas: PsdCanvasSize,
  background = '#ffffff',
  scale = 1,
  transform: ThumbnailTransform = {}
) => {
  const image = await loadImageFromBlob(sourceBlob);
  const output = document.createElement('canvas');
  output.width = 300;
  output.height = 300;
  const context = output.getContext('2d');
  if (!context) throw new Error('缩略图画布初始化失败');

  context.fillStyle = background;
  context.fillRect(0, 0, 300, 300);
  const sourceScale = Math.min(270 / bounds.width, 270 / bounds.height) * scale;
  const drawWidth = canvas.width * sourceScale;
  const drawHeight = canvas.height * sourceScale;
  const contentCenterX = (bounds.x + bounds.width / 2) * sourceScale;
  const contentCenterY = (bounds.y + bounds.height / 2) * sourceScale;
  const drawX = 150 - contentCenterX + (transform.offsetX || 0);
  const drawY = 150 - contentCenterY + (transform.offsetY || 0);
  context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  return canvasToBlob(output);
};

export const compressPngUnderLimit = async (blob: Blob, maxBytes = dressOriginMaxBytes, message = '图片压缩失败') => {
  const shouldNormalizePng = blob.type !== 'image/png';
  if (!shouldNormalizePng && blob.size <= maxBytes) return blob;
  const image = await loadImageFromBlob(blob);
  let width = image.naturalWidth;
  let height = image.naturalHeight;
  let current = blob;

  if (shouldNormalizePng) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('图片压缩失败');
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    current = await canvasToBlob(canvas);
  }

  while (current.size > maxBytes) {
    width = Math.max(1, Math.floor(width * pngScaleStep));
    height = Math.max(1, Math.floor(height * pngScaleStep));
    if (Math.max(width, height) <= pngMinSide) throw new Error(message);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('图片压缩失败');
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    current = await canvasToBlob(canvas);
  }

  if (current.size > maxBytes) throw new Error(message);
  return current;
};
